import React, { FC, useState } from 'react';
import { Form, FormGroup, Input, Label, Spinner } from 'reactstrap';
import { Button, Datepicker } from '@bphxd/ds-core-react';
import { Check32, Info32 } from '@bphxd/ds-core-react/lib/icons';
import { useLazyQuery } from '@apollo/client';
import { EMPTY_EVENT } from '../../../lib/constants';
import { TEvent } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { GET_MO_COUNT } from '../../../graph-queries/tar/job';
import { renderOptions } from '../../../lib/util/render';
import { EVENT_TYPE_ENUM } from '../../../lib/constants/dbConstants';

const EventForm: FC<{
  defaultValues?: TEvent;
  onSubmit: (event: TEvent) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const [eventStartDate, setEventStartDate] = useState(defaultValues?.eventStartDate || 0);
  const [eventEndDate, setEventEndDate] = useState(defaultValues?.eventEndDate || 0);
  const [eventScopeFreezeDate, setEventScopeFreezeDate] = useState(
    defaultValues?.eventScopeFreezeDate || 0
  );
  const [revisionCode, setRevisionCode] = useState(defaultValues?.revisionCode || '');
  const [jobCount, setJobCount] = useState(0);

  const [getMoCount, { data: moCountData, loading }] = useLazyQuery(GET_MO_COUNT);

  const handleRevisionCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRevisionCode(e.target.value);
  };

  const handleSearch = (revCode: string) => {
    getMoCount({ variables: { assetId: asset?.assetId || -1, revisionCode: revCode } })
      .then((res) => {
        if (res.error) {
          console.log(res.error);
        } else {
          setJobCount(res.data?.sapMoCount || 0);
        }
      })
      .catch((error) => {
        console.log(error);
      });
    return;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    // Prevent default behavior
    e.preventDefault();

    const data = new FormData(e.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string>;

    const event: TEvent = {
      ...EMPTY_EVENT,
      ...inputData,
      assetId: asset?.assetId || -1,
      eventStartDate,
      eventEndDate,
      eventScopeFreezeDate,
    };
    if (defaultValues?.eventId) event.eventId = defaultValues.eventId;
    if (defaultValues?.assetId) event.assetId = defaultValues.assetId;

    onSubmit(event)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };

  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-6">
        <FormGroup>
          <Label for="event-description">Event Name</Label>
          <Input
            type="text"
            name="eventDescription"
            id="eventDescription"
            required
            defaultValue={defaultValues?.eventDescription || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="event-date-of-scope-freeze">Scope Freeze Date</Label>
          <Datepicker
            placeholder="Select scope freeze date"
            options={{
              defaultDate: eventScopeFreezeDate,
              onChange: ([date]: [Date]) => setEventScopeFreezeDate(date.valueOf()),
            }}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="event-start">Start Date</Label>
          <Datepicker
            placeholder="Select start date"
            options={{
              defaultDate: eventStartDate,
              onChange: ([date]: [Date]) => setEventStartDate(date.valueOf()),
            }}
            invalid={eventStartDate > eventEndDate}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="event-end">End Date</Label>
          <Datepicker
            placeholder="Select end date"
            options={{
              defaultDate: eventEndDate,
              onChange: ([date]: [Date]) => setEventEndDate(date.valueOf()),
            }}
            invalid={eventStartDate > eventEndDate}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="event-type">Event Type</Label>
          <Input
            type="select"
            name="eventType"
            id="event-type"
            role="combobox"
            defaultValue={defaultValues?.eventType || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(EVENT_TYPE_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div>
        <FormGroup>
          <Label for="revision-code">Revision Code</Label>
          <div className="d-flex">
            <Input
              type="text"
              name="revisionCode"
              id="revision-code"
              placeholder="Enter a revision code to add MOs to event"
              defaultValue={revisionCode}
              onChange={handleRevisionCodeChange}
            />
            <Button
              type="button"
              level="tertiary"
              rounded="0"
              onClick={() => handleSearch(revisionCode)}
            >
              Search
            </Button>
          </div>
        </FormGroup>
      </div>

      {loading && (
        <div className="d-flex justify-content-center">
          <Spinner />
        </div>
      )}

      {moCountData && jobCount > 0 && (
        <div className="d-flex flex-column justify-content-center align-items-center mt-6">
          <Check32 className="text-success" />
          <p>
            {jobCount} MO{jobCount > 1 ? 's' : ''} Found
          </p>
          <p className="small">Create your event to create jobs for these MOs</p>
        </div>
      )}

      {moCountData && jobCount === 0 && (
        <div className="d-flex flex-column justify-content-center align-items-center mt-6">
          <Info32 />
          <p>No Results Found</p>
          <p className="small">We couldn&apos;t find any MOs with that revision code</p>
        </div>
      )}

      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new event
          </Button>
        </div>
      ) : (
        <>
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default EventForm;
